import { useMutation } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useErrors } from '../../../../hooks/errors/use-errors';
import { useNative } from '../../../../hooks/native/use-native';
import { useHttpClient } from '../../../../utils/clients/rest-client';
import {
  LightSetBrightnessData,
  LightSetOnTimeData,
  LightSetRgbData,
  LightSetTemperatureData,
  LightSupplyReturnBehaviorData,
  LightSupplyReturnBehaviorError,
  LightToggleOffData,
  LightToggleOnData,
  SetBrightnessRequest,
  SetOnTimeRequest,
  SetRgbRequest,
  SetSupplyReturnBehaviourRequest,
  SetTemperatureRequest,
  ToggleOnOffRequest,
} from '../../types/openapi/openapi-types';

export const useLightToggleOn = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<LightToggleOnData>, AxiosError<void>, ToggleOnOffRequest>(
    ['light-toggleOn'],
    async (body) => {
      fireVibration();
      return await httpClient.light.lightToggleOn(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useLightToggleOff = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<LightToggleOffData>, AxiosError<void>, ToggleOnOffRequest>(
    ['light-toggleOff'],
    async (body) => {
      fireVibration();
      return await httpClient.light.lightToggleOff(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useLightSetOnTime = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<LightSetOnTimeData>, AxiosError<void>, SetOnTimeRequest>(
    ['light-setOnTime'],
    async (body) => {
      fireVibration();
      return await httpClient.light.lightSetOnTime(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useLightSetBrightness = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<LightSetBrightnessData>, AxiosError<void>, SetBrightnessRequest>(
    ['light-setBrightness'],
    async (body) => {
      fireVibration();
      return await httpClient.light.lightSetBrightness(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useLightSetTemperature = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<LightSetTemperatureData>, AxiosError<void>, SetTemperatureRequest>(
    ['light-setTemperature'],
    async (body) => {
      fireVibration();
      return await httpClient.light.lightSetTemperature(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useLightSetRgb = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<LightSetRgbData>, AxiosError<void>, SetRgbRequest>(
    ['light-setRgb'],
    async (body) => {
      fireVibration();
      return await httpClient.light.lightSetRgb(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useLightSupplyReturnBehaviour = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<LightSupplyReturnBehaviorData>,
    AxiosError<LightSupplyReturnBehaviorError>,
    SetSupplyReturnBehaviourRequest
  >(['light-supplyReturnBehavior'], async (body) => await httpClient.light.lightSupplyReturnBehavior(body), {
    onError: (error) => {
      handleRestErrors(error);
    },
  });
};
